import { uniqueId } from '../utils/uniqueId';
import { cloudflareTurnstileService } from "../services/botProtection/cloudflareTurnstileService";
import DOMPurify from 'dompurify';
import ErrorReporter from "../../assets/js/Service/errorReporter";
import { GuideChatManager } from "./guideChatManager";

// On backend side, we have the same constant
// Keep in sync with backend constants if changed
const MIN_QUESTIONS_FOR_NO_CF_CHALLENGE = 1;

export class AiChat {
    constructor($el) {
        this.$el = $el;
        this.allQuestions = [];
        this.chatId = null;
        this.isMobile = this.setIsMobile()
        this.apiEndpoint = this.$el.dataset.endpoint;
        this.guideManager = null;

        try {
            this.apiPayload = JSON.parse(this.$el.dataset.chatPayload);
        } catch (error) {
            this.apiPayload = {};
        }
    }

    init() {
        if (this.$el.isActiveChatExistsOnPage) return;
        this.$el.isActiveChatExistsOnPage = true;

        this.$form = $(this.$el).find('.js_ai-form');
        this.$chatBox = $(this.$el).find('.chat-box');
        this.$question = this.$form.find('input[name="question"]');
        const $submitButton = this.$form.find('button[type="submit"]');

        if (this.isGuideChat()) {
            this.$chatBox.addClass('chat-box--guide');
            this.guideManager = new GuideChatManager(this);
            this.guideManager.restoreHistory();
        }

        const addChatBoxResizeListener = this.addChatBoxResizeListener.bind(this);
        addChatBoxResizeListener(this.$chatBox[0]);

        this.$form[0].addEventListener('submit', (event) => {
            event.preventDefault();

            if (!this.$question.val().trim()) return;

            $submitButton.prop('disabled', true);
            $submitButton.css({
                'background-color': '#ccc',
                'color': '#666',
                'cursor': 'not-allowed'
            });

            this.allQuestions.push(this.$question.val());

            const userBubble = $('<div>', {
                class: 'chat-bubble user',
                text: this.$question.val(),
                'data-role': 'user',
            });
            this.$chatBox.append(userBubble);
            this.$chatBox.removeClass('hidden');
            this.scrollToBottom();

            const startTime = Date.now();

            if (this.shouldSkipChallenge()) {
                this.handleQuestion($submitButton, startTime)

                return;
            }

            cloudflareTurnstileService.submitFormWithChallenge(
                this.$form,
                () => this.handleQuestion($submitButton, startTime),
                true
            );
        });
    }

    shouldSkipChallenge() {
        return this.allQuestions.length > MIN_QUESTIONS_FOR_NO_CF_CHALLENGE;
    }

    addChatBoxResizeListener($chatBox) {
        let chatboxHeight = $chatBox.offsetHeight;

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (chatboxHeight === entry.contentRect.height) return;
                chatboxHeight = entry.contentRect.height;
                this.scrollToBottom();
            }
        });

        resizeObserver.observe($chatBox);
        return resizeObserver;
    }


    createThinkingBubble(hidden = true) {
        const botBubble = $('<div>', {
            class: `chat-bubble bot typing-indicator ${hidden ? 'hidden' : ''}`,
            'data-role': 'system',
        });

        for (let i = 0; i < 3; i++) {
            botBubble.append('<span></span>');
        }

        return botBubble;
    }

    setIsMobile() {
        const mq = window.matchMedia('(max-width: 740px)');
        return mq.matches;
    }

    isGuideChat() {
        return this.apiEndpoint.includes('guide');
    }

    isStreamEndpoint() {
        return this.apiEndpoint.includes('stream');
    }

    scrollToBottom() {
        if (this.isGuideChat() && this.guideManager) {
            this.guideManager.scrollToBottom();
            return;
        }

        if (!this.$question || !this.$question[0]) return;

        const rect = this.$question[0].getBoundingClientRect();
        const offset = 40;
        const scrollPosition = window.pageYOffset + rect.top + rect.height - window.innerHeight + offset;

        window.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: 'smooth'
        });
    }

    handleQuestion($submitButton, startTime) {
        if (this.isStreamEndpoint()) {
            return this.handleStreamQuestion($submitButton);
        }

        const botBubble = this.createThinkingBubble(); // chat thinking (hidden)
        this.$chatBox.append(botBubble);

        const minDelay = new Promise((resolve) => setTimeout(resolve, 1000));

        const thinkingShowBubbleDelay = new Promise((resolve) => {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(100 - elapsedTime);

            return setTimeout(() => {
                botBubble.removeClass('hidden');
                this.scrollToBottom();
                resolve();
            }, remainingTime)
        });

        const apiCall = $.ajax({
            url: this.apiEndpoint,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            data: {
                ...this.apiPayload, // Defaults to empty object
                question: this.$question.val(),
                previousQuestions: this.allQuestions.slice(0, -1),
                'cf-turnstile-response': this.$form.find('input[name="cf-turnstile-response"]').val(),
                customerId: window.customer?.regId ?? 0,
                ...(window.comparisonId && {
                    comparisonId: window.comparisonId,
                    questionerId: this.getQuestionerId(),
                }),
                ...(this.chatId && { chatId: this.chatId }),
            },
            type: 'post',
        })

        return Promise.all([apiCall, minDelay, thinkingShowBubbleDelay])
            .then(([data, _delayArg, _bubbleArg]) => {
                this.$question.val('');
                this.messageUuid = data.message_uuid;
                this.chatId ||= data.chatId;

                let answer = DOMPurify.sanitize(data.answer);
                // Adds target="_blank" to <a> tags that already have rel="noopener noreferrer"
                answer = answer.replace(
                    /<a([^>]*rel="noopener noreferrer"[^>]*)>/g,
                    (match, p1) => {
                        return `<a${p1} target="_blank">`;
                    }
                );

                botBubble.html(answer);
                botBubble.removeClass('typing-indicator');

                // Show product cards in chat if any were provided
                data.products?.length && this.$chatBox.append(data.products);

                if (this.isGuideChat() && this.guideManager) {
                    this.guideManager.handleResetButton();
                    setTimeout(() => this.scrollToBottom(), 100);
                }

                $submitButton.prop('disabled', false);
                $submitButton.css({
                    'background-color': '',
                    'color': '',
                    'cursor': ''
                });

            })
            .catch((error) => {
                botBubble.text('Sorry, ik kan je vraag op dit moment niet beantwoorden. Probeer het later nog eens.');

                ErrorReporter.report(error, {
                    questions: this.allQuestions,
                });
            })
            .finally(() => {
                if (! this.shouldSkipChallenge()) {
                    cloudflareTurnstileService.clearTurnstileContainer();
                }
            })
    }

    handleStreamQuestion($submitButton) {
        const botBubble = this.createThinkingBubble(false);
        this.$chatBox.append(botBubble);
        this.scrollToBottom();

        const formData = new FormData();

        Object.entries(this.apiPayload || {}).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((item) => formData.append(`${key}[]`, item));
            } else {
                formData.append(key, value);
            }
        });

        formData.append('question', this.$question.val());

        this.allQuestions.slice(0, -1).forEach((q) => {
            formData.append('previousQuestions[]', q);
        });

        formData.append('customerId', window.customer?.regId ?? 0);

        const cfToken = this.$form.find('input[name="cf-turnstile-response"]').val();
        if (cfToken) {
            formData.append('cf-turnstile-response', cfToken);
        }

        if (this.chatId) {
            formData.append('chatId', this.chatId);
        }

        this.$question.val('');

        const enableSubmitButton = () => {
            $submitButton.prop('disabled', false);
            $submitButton.css({
                'background-color': '',
                'color': '',
                'cursor': ''
            });
        };

        let fullAnswer = '';

        fetch(this.apiEndpoint, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
        })
            .then((response) => {
                if (! response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                if (! response.body) {
                    throw new Error('Response body is not available');
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let isFirstChunk = true;

                const read = () => {
                    reader.read().then(({ done, value }) => {
                        if (! done) {
                            buffer += decoder.decode(value, { stream: true });
                        }

                        const events = buffer.split('\n\n');
                        buffer = done ? '' : events.pop();

                        for (const event of events) {
                            if (!event.trim()) continue;

                            const lines = event.split('\n');
                            let eventType = null;
                            const dataLines = [];

                            for (const line of lines) {
                                if (line.startsWith('event: ')) {
                                    eventType = line.slice(7);
                                } else if (line.startsWith('data: ')) {
                                    dataLines.push(line.slice(6));
                                } else if (line === 'data:') {
                                    dataLines.push('');
                                }
                            }

                            const data = dataLines.join('\n');

                            if (eventType === 'done') {
                                try {
                                    const parsed = JSON.parse(data);
                                    this.chatId ||= parsed.chatId;
                                } catch (e) { /* ignore parse errors */ }
                            } else if (eventType === 'error') {
                                throw new Error(data);
                            } else if (data) {
                                if (isFirstChunk) {
                                    botBubble.removeClass('typing-indicator');
                                    botBubble.text('');
                                    isFirstChunk = false;
                                }

                                fullAnswer += data;
                                botBubble.append(document.createTextNode(data));
                                this.scrollToBottom();
                            }
                        }

                        if (done) {
                            this.finalizeSanitizedAnswer(botBubble, fullAnswer);
                            enableSubmitButton();
                            return;
                        }

                        read();
                    }).catch((error) => {
                        if (fullAnswer.trim()) {
                            this.finalizeSanitizedAnswer(botBubble, fullAnswer);
                        } else {
                            botBubble.removeClass('typing-indicator');
                            botBubble.text('Sorry, ik kan je vraag op dit moment niet beantwoorden. Probeer het later nog eens.');
                        }

                        enableSubmitButton();

                        ErrorReporter.report(error, { questions: this.allQuestions });
                    });
                };

                read();
            })
            .catch((error) => {
                botBubble.removeClass('typing-indicator');
                botBubble.text('Sorry, ik kan je vraag op dit moment niet beantwoorden. Probeer het later nog eens.');
                enableSubmitButton();

                ErrorReporter.report(error, { questions: this.allQuestions });
            })
            .finally(() => {
                if (! this.shouldSkipChallenge()) {
                    cloudflareTurnstileService.clearTurnstileContainer();
                }
            });
    }

    convertMarkdownToHtml(markdown) {
        // Convert headings (# to ######) to bold
        let html = markdown.replace(/^(#{1,6})\s+(.+)$/gm, '<b>$2</b>');
        // Convert **text** and __text__ to bold
        html = html.replace(/(\*\*|__)(.*?)\1/g, '<b>$2</b>');
        // Convert [text](url) to links
        html = html.replace(
            /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
            '<a href="$2" class="textLink" rel="noopener noreferrer" target="_blank">$1</a>'
        );
        // Convert newlines to <br>
        html = html.replace(/\n/g, '<br>');

        return html;
    }

    finalizeSanitizedAnswer(botBubble, fullAnswer) {
        if (!fullAnswer) return;

        const html = this.convertMarkdownToHtml(fullAnswer);
        const sanitized = DOMPurify.sanitize(html);

        botBubble.html(sanitized);
    }

    getQuestionerId() {
        const key = 'questionerId';
        let questionerId = localStorage.getItem(key);

        if (! questionerId) {
            questionerId = uniqueId();
            localStorage.setItem(key, questionerId);
        }

        return questionerId;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.js_ai-chat').forEach(($el) => {
        const aiChat = new AiChat($el)
        aiChat.init()
    })
});
